import { useLayoutEffect, type RefObject } from "react";
import { useAppStore } from "@/stores";
import { applyTheme } from "@/utils/apply-theme";

/**
 * Subscribes to the theme in Zustand and applies CSS custom properties to
 * the appropriate root element. In Shadow DOM it targets the host element;
 * in regular DOM it targets `<html>`.
 *
 * Pass a ref to any mounted element inside the widget tree so the hook can
 * call `getRootNode()` to auto-detect the rendering context.
 */
export function useApplyTheme(ref: RefObject<HTMLElement | null>) {
  const theme = useAppStore((state) => state.theme);

  // useLayoutEffect (not useEffect): apply the socket palette to the host
  // *before* the browser paints, so the widget never flashes the default
  // :host colors for a frame before the theme lands.
  useLayoutEffect(() => {
    if (!theme || !ref.current) return;

    const rootNode = ref.current.getRootNode();
    const container =
      rootNode instanceof ShadowRoot
        ? (rootNode.host as HTMLElement)
        : document.documentElement;

    applyTheme(container, theme);
  }, [theme, ref]);
}
